/**
 * Save an already-fetched blob to disk.
 *
 * A blob rather than an anchor pointing at the API: the frontend talks to the backend
 * cross-origin, so a plain `href` would ignore the `download` attribute — and for files the
 * server refuses to publish a URL for, there is no href to point at in the first place.
 */
export const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on a delay: revoking synchronously can cancel the download in some browsers before
  // it has read the object.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * The name the server asked the file to be saved as, from Content-Disposition.
 *
 * Worth reading rather than hardcoding: the server already sanitises and falls back, so whatever
 * arrives here is both safe and the name the uploader chose. A hardcoded name gives every download
 * the same one, which turns a folder of care plans into care-plan.pdf, care-plan (1).pdf, and so on
 * with nothing to tell them apart.
 *
 * Only the RFC 5987 `filename*=UTF-8''…` form is percent-encoded, so only that one is decoded.
 * Decoding the plain form would corrupt any name containing a literal percent — `50% off.pdf`
 * throws URIError, which would have taken the download down with it. A malformed encoded name
 * falls back to its raw text for the same reason: a slightly wrong file name beats no file.
 *
 * Content-Disposition is only readable cross-origin because the API lists it in
 * Access-Control-Expose-Headers; without that this always returns the fallback.
 */
export const fileNameFromDisposition = (
  disposition: string | undefined,
  fallback: string,
): string => {
  const encoded = /filename\*=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition ?? '');
  if (encoded?.[1]?.trim()) {
    const raw = encoded[1].trim();
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(disposition ?? '');
  return plain?.[1]?.trim() || fallback;
};
